"use client";

import React from "react";
import { PenTool, Cpu, ShoppingCart, Hammer, Settings, ArrowRight } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
const lifecycleSteps = [{
  id: "phase-1",
  code: "SYS.01",
  phase: "Planning",
  title: "Master Planning & Concept",
  description: "Feasibility studies, conceptual master planning, and schematic design for large-scale cultural tourism destinations.",
  metric: "Vision to Blueprint",
  icon: "PenTool"
}, {
  id: "phase-2",
  code: "SYS.02",
  phase: "ENGINEERING",
  title: "Technical & Structural",
  description: "Detailed engineering blueprints, structural analysis, and compliance with international safety standards for amusement facilities.",
  metric: "Precision & Safety",
  icon: "Cpu"
}, {
  id: "phase-3",
  code: "SYS.03",
  phase: "PROCUREMENT",
  title: "Global Sourcing",
  description: "Strategic sourcing of specialized ride systems, thematic materials, and construction equipment across Belt and Road networks.",
  metric: "Supply Chain Optimization",
  icon: "ShoppingCart"
}, {
  id: "phase-4",
  code: "SYS.04",
  phase: "CONSTRUCTION",
  title: "Project Management",
  description: "On-site construction management, quality control, and timeline execution ensuring high-end corporate standards.",
  metric: "Build & Execute",
  icon: "Hammer"
}, {
  id: "phase-5",
  code: "SYS.05",
  phase: "OPERATIONS",
  title: "Destination Management",
  description: "Post-launch operational strategy, staff training, maintenance protocols, and continuous profitability optimization.",
  metric: "Sustainable Growth",
  icon: "Settings"
}];
const iconMap: Record<string, React.ElementType> = {
  PenTool,
  Cpu,
  ShoppingCart,
  Hammer,
  Settings
};
export default function TheFullLifecycleModel() {
  return <section id="the-full-lifecycle-model" className="w-full py-24 overflow-hidden relative !bg-[hsl(0,0%,100%)] !bg-[url(https://perfect-spider.10web.cloud/wp-content/uploads/2026/03/25.jpg)] ] !text-[hsl(202,80%,24%)]" style={{
    backgroundImage: "url(https://perfect-spider.10web.cloud/wp-content/uploads/2026/03/themepark_masterplan.jpg)"
  }}>
      <div className="absolute inset-0 !bg-transparent !bg-[url(https://perfect-spider.10web.cloud/wp-content/uploads/2026/03/25.jpg)]"></div>
      
      <div className="relative z-10 px-4 md:px-8 lg:px-12">
        <div className="mb-20 max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-6 border border-border bg-muted/50 rounded-sm">
            <span className="size-2 bg-primary rounded-full animate-pulse" />
            <span className="text-xs font-mono text-muted-foreground tracking-wider uppercase">Integrated EPCO Services</span>
          </div>
          <h2 className="mb-6 tracking-tight !text-6xl !opacity-[1] !bg-transparent !text-[hsl(0,0%,0%)]">The Full-Lifecycle Model</h2>
          <p className="text-lg font-sans max-w-2xl !text-[hsl(202,80%,24%)]">A comprehensive, end-to-end approach to cultural tourism development. Click on each node to explore the technical specifications of our integrated value chain.</p>
        </div>

        <div className="relative mt-12 pb-12">
          
          <div className="absolute left-[27px] top-0 bottom-0 w-px bg-border md:left-0 md:top-[45px] md:bottom-auto md:w-full md:h-px z-0" />

          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-12 md:gap-4 relative z-10">
            {lifecycleSteps.map((step, i) => {
            const IconComponent = iconMap[step.icon];
            return <div key={step.id} data-index={i} className="flex flex-row md:flex-col items-start md:items-center gap-6 md:gap-4 w-full md:w-1/5 group">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="icon" className="relative size-14 rounded-none border-2 border-background bg-muted hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all duration-300 shadow-sm shrink-0 z-10" aria-label={`View details for ${step.phase}`}>
                        <IconComponent className="size-6" />
                        <span className="absolute -inset-2 border border-transparent group-hover:border-primary/30 rounded-none transition-colors duration-300" />
                      </Button>
                    </PopoverTrigger>
                    
                    <PopoverContent className="w-80 p-0 rounded-none border-border shadow-xl bg-card" sideOffset={16} align="start">
                      <div className="p-4 border-b border-border bg-muted/30 flex justify-between items-center">
                        <span className="font-mono text-xs text-muted-foreground">{step.code}</span>
                        <span className="font-mono text-xs font-semibold text-primary">{step.phase}</span>
                      </div>
                      <div className="p-5">
                        <h4 className="font-sans font-semibold text-foreground text-lg mb-2">
                          {step.title}
                        </h4>
                        <p className="text-sm text-muted-foreground font-sans leading-relaxed mb-4">
                          {step.description}
                        </p>
                        <div className="pt-4 border-t border-border/50 flex items-center justify-between">
                          <span className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Output</span>
                          <span className="text-xs font-mono text-foreground">{step.metric}</span>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>

                  <div className="flex flex-col md:items-center text-left md:text-center mt-1 md:mt-2">
                    <span className="text-muted-foreground mb-1 tracking-widest !text-xl !font-sans">
                      0{i + 1}
                    </span>
                    <h3 className="font-medium text-foreground tracking-tight uppercase !text-xl !font-sans">
                      {step.phase}
                    </h3>
                    
                    <p className="text-sm text-muted-foreground mt-2 md:hidden max-w-[250px]">
                      {step.title}
                    </p>
                  </div>
                  
                  {i < lifecycleSteps.length - 1 && <div className="hidden md:flex absolute top-[45px] left-0 w-full justify-center -z-10 opacity-0">
                    </div>}
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}